import type { ReactElement } from 'react';
import { registerPlatformDriver } from '@wu/widget-platform';
import type { WidgetComponent } from './create-widget';
import { createWidget } from './create-widget';
import type { SettingsProviderAPI } from './settings-context';
import type { WidgetSettings } from './widget-settings.interface';

export type ApplicationMeta = {
  alias: string;
  name: string;
};

export class Application {
  meta: ApplicationMeta;
  WidgetComponent: WidgetComponent<WidgetSettings>;
  api: SettingsProviderAPI | null = null;

  constructor(
    meta: ApplicationMeta,
    WidgetComponent: WidgetComponent<WidgetSettings>
  ) {
    this.meta = meta;
    this.WidgetComponent = WidgetComponent;

    this.registerProviders();
  }

  static init<TSettings extends WidgetSettings>(
    meta: ApplicationMeta,
    WidgetComponent: WidgetComponent<TSettings>
  ) {
    return new this(meta, WidgetComponent as WidgetComponent<WidgetSettings>);
  }

  registerProviders() {
    registerPlatformDriver(this.createWidget, this.meta.alias);
  }

  createWidget = async (
    container: HTMLElement,
    initialSettings = {},
    render?: (widget: WidgetComponent<WidgetSettings>) => ReactElement
  ): Promise<SettingsProviderAPI> => {
    this.api = await createWidget(
      container,
      this.WidgetComponent,
      render,
      initialSettings
    );

    return this.api;
  };
}

export const createApp = <TSettings extends WidgetSettings>(
  meta: ApplicationMeta,
  Widget: WidgetComponent<TSettings>
) => Application.init(meta, Widget);
